import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ImageOff, VideoOff } from 'lucide-react';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { Skeleton } from 'v5/design-system/Skeleton';
import { cn } from 'v5/platform/dom/cn';
import { keepAnimationInJs } from 'v5/platform/react/keepAnimationInJs';

export interface MediaLoadPendingOverlayProps {
  className?: string;
}

export function MediaLoadPendingOverlay({
  className
}: MediaLoadPendingOverlayProps) {
  return (
    <div
      className={cn(
        'absolute inset-0 flex items-center justify-center',
        className
      )}
    >
      <Skeleton className="absolute inset-0 size-full" />
      <motion.div
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, delay: 0.1 }}
        onUpdate={keepAnimationInJs}
      >
        <LoadingSpinner className="relative size-8 text-fg-muted" />
      </motion.div>
    </div>
  );
}

export interface MediaLoadErrorOverlayProps {
  className?: string;
  messageClassName?: string;
  /** Image vs video copy and icon (`ImageOff` / `VideoOff`). @default 'image' */
  variant?: 'image' | 'video';
}

export function MediaLoadErrorOverlay({
  className,
  messageClassName,
  variant = 'image'
}: MediaLoadErrorOverlayProps) {
  const { t } = useTranslation('patterns');
  const Icon = variant === 'video' ? VideoOff : ImageOff;
  const messageKey =
    variant === 'video'
      ? 'MediaLoadStateOverlays.videoUnavailable'
      : 'MediaLoadStateOverlays.imageUnavailable';
  return (
    <div
      className={cn(
        'absolute inset-0 flex flex-col items-center justify-center gap-2 bg-bg-subtle p-4 text-center text-fg-muted',
        className
      )}
    >
      <Icon
        className="size-10 shrink-0 opacity-60"
        strokeWidth={1.5}
        aria-hidden
      />
      <span className={cn('text-xs font-medium', messageClassName)}>
        {t(messageKey)}
      </span>
    </div>
  );
}
